import React, { useRef } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Table } from "react-bootstrap";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const utahData = "/img/headerlogo.png";
const bodex = "/img/bodexLogo.png";
const kpi = "/img/KPI-DASH 2.png";
const pdfIcon = "/img/pdf1.png";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

export default function CsAndItTable4({ data }) {
  const pdfRef = useRef();

  if (!data || data.length === 0) return <p>No data available</p>;

  const years = data.map((item) => item.year);
  const avgFemale = data.map((item) => parseFloat(item.avg_female_employee));
  const avgMale = data.map((item) => parseFloat(item.avg_male_employee));

  // Summary Metrics
  const femalePeak = Math.max(...avgFemale);
  const malePeak = Math.max(...avgMale);

  const femalePeakYear = years[avgFemale.indexOf(femalePeak)];
  const malePeakYear = years[avgMale.indexOf(malePeak)];

  const femaleAvg = (
    avgFemale.reduce((a, b) => a + b, 0) / avgFemale.length
  ).toFixed(0);
  const maleAvg = (avgMale.reduce((a, b) => a + b, 0) / avgMale.length).toFixed(
    0
  );

  const chartData = {
    labels: years,
    datasets: [
      {
        label: "Average Female Employees",
        data: avgFemale,
        backgroundColor: "#EC4899",
        borderColor: "#EC4899",
        borderWidth: 1,
      },
      {
        label: "Average Male Employees",
        data: avgMale,
        backgroundColor: "#0ea5e9",
        borderColor: "#0ea5e9",
        borderWidth: 1,
      },
    ],
  };

  const chartOptions = {
    indexAxis: "y",
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" },
      title: {
        display: true,
        text: "Avg. Female vs Male Employment Distribution (2018–2024)",
        font: { size: 16, weight: "bold" },
      },
    },
    scales: {
      x: {
        title: { display: true, text: "Number of Employees", font: { size: 14, weight: "bold" } },
      },
      y: {
        title: {
          display: true,
          text: "Year",
          font: { size: 14, weight: "bold" },
        },
        title: { display: true, text: "Year" },
      },
    },
  };

  // ---- PDF EXPORT ----
    const downloadPDF = () => {
      const input = pdfRef.current;
      html2canvas(input, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
        scrollY: -window.scrollY,
      }).then((canvas) => {
        const imgData = canvas.toDataURL("image/png");
        const pdf = new jsPDF("p", "mm", "a4");
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const imgWidth = pageWidth - 10;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        let heightLeft = imgHeight;
        let position = 0;
        let pageNumber = 1;

        const addHeader = () => {
          pdf.addImage(utahData, "PNG", 60, 10, 32, 16);
          pdf.setFontSize(26);
          pdf.text("UTahData.org", 95, 22);
          pdf.link(50, 15, 60, 10, { url: "https://utahdata.org" });
          pdf.line(10, 35, 200, 35);
        };

        const addFooter = (pageNumber) => {
          pdf.setFontSize(9);
          const footerY = pageHeight - 10;
          pdf.line(10, footerY - 5, pageWidth - 10, footerY - 5);
          pdf.text(
            `Downloaded on: ${new Date().toLocaleDateString()}`,
            10,
            footerY
          );
          pdf.text(
            `Page ${pageNumber}`,
            pageWidth - 10,
            footerY,
            null,
            null,
            "right"
          );
        };

        // First page
        addHeader();
        pdf.addImage(imgData, "PNG", 5, 32, imgWidth, imgHeight);
        heightLeft -= pageHeight - 32;

        // Add more pages if needed
        while (heightLeft > 0) {
          pdf.addPage();
          pageNumber++;
          addHeader();
          position = heightLeft - imgHeight;
          pdf.addImage(imgData, "PNG", 5, position + 32, imgWidth, imgHeight);
          heightLeft -= pageHeight - 32;
          addFooter(pageNumber);
        }

        // Final footer
        pdf.line(10, 260, 200, 260);
        const footerY = 265;
        pdf.addImage(utahData, "PNG", 22, footerY, 30, 15);
        pdf.setFontSize(10);
        pdf.text("Built by", 55, footerY + 10);
        pdf.addImage(bodex, "PNG", 68, footerY + 4, 44, 10);
        pdf.text("using", 112, footerY + 10);
        pdf.addImage(kpi, "PNG", 125, footerY, 20, 20);
        pdf.text("20250916.v3.198", 148, footerY + 10);
        pdf.text(`Downloaded on: ${new Date().toLocaleDateString()}`, 150, 295);
        pdf.text(`From: ${window.location.href}`, 10, 295);

        pdf.save("cs_it_gender_average.pdf");
      });
    };

  return (
    <>
      <div ref={pdfRef} className="p-6 bg-white shadow-md rounded-xl">
        <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">
          Average Female vs Male Employment (2018–2024)
        </h2>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 text-center">
          <div className="p-4 bg-blue-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">Avg Female</p>
            <p className="text-xl font-bold text-blue-900">
              {Number(femaleAvg).toLocaleString("en-US")}
            </p>
          </div>

          <div className="p-4 bg-red-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">Avg Male</p>
            <p className="text-xl font-bold text-red-900">
              {Number(maleAvg).toLocaleString("en-US")}
            </p>
          </div>

          <div className="p-4 bg-green-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Peak Employment Year
            </p>
            <p className="text-xl font-bold text-green-900">
              Male: {malePeakYear} | Female: {femalePeakYear}
            </p>
          </div>
        </div>

        {/* TABLE + CHART */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
          {/* Table */}
          <div className="flex flex-col justify-center">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-center shadow-md rounded-lg overflow-hidden">
                <thead className="bg-gray-800 text-white">
                  <tr>
                    <th className="p-2 text-left">Year</th>
                    <th className="p-2" style={{ textAlign: 'right' }}>Avg Female</th>
                    <th className="p-2" style={{ textAlign: 'right' }}>Avg Male</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((row, i) => (
                    <tr key={i} className="hover:bg-gray-100 transition p-2">
                      <td className="p-2 text-left">{row.year}</td>
                      <td className="font-bold text-blue-800 p-2" style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                        {Number(row.avg_female_employee).toLocaleString(
                          "en-US"
                        )}
                      </td>
                      <td className="font-bold text-red-800 p-2" style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                        {Number(row.avg_male_employee).toLocaleString("en-US")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Chart */}
          <div className="flex flex-col w-full overflow-x-auto bg-white rounded-lg shadow-md">
            <div className="min-w-[400px] w-full h-96 p-4">
              <Bar data={chartData} options={chartOptions} />
            </div>
          </div>
        </div>
      </div>

      {/* Download Button */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg hover:scale-105 transition-transform"
        >
          Download as PDF
          <img className="w-[1.5rem]" src={pdfIcon} alt="pdf icon" />
        </button>
      </div>
    </>
  );
}
